import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

const AppTermsOfService = () => {
  return (
    <div className="min-h-screen relative bg-[#0A0A0A] text-white">
      {/* Fixed Background Elements */}
      <div className="fixed inset-0 z-0">
        {/* Background Image Layer */}
        <img
          src="/images/headerbg.png"
          alt=""
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1600px] max-w-none h-full object-cover opacity-30 object-top"
        />
        {/* Primary Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-[#111111]/95 to-[#111111]" />
        {/* Accent Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-emerald-500/[0.03] to-transparent" />
        
        {/* Gradient Orbs */}
        <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 bg-emerald-500/[0.07]" />
        <div className="fixed bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 bg-emerald-500/[0.07]" />
      </div>

      {/* Fixed Back Button - stays visible at the top */}
      <div className="fixed top-0 left-0 z-50 w-full" style={{ paddingTop: 'calc(env(safe-area-inset-top))', paddingLeft: '1rem', background: 'linear-gradient(to bottom, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0.6) 60%, rgba(10,10,10,0) 100%)', paddingBottom: '1.5rem' }}>
        <div className="pt-4 pb-2">
          <Link
            to="/login"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 bg-white/10 hover:bg-white/20"
          >
            <ChevronLeft className="text-white" size={20} />
          </Link>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="relative z-10 min-h-screen overflow-y-auto" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 4rem)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}>
        <div className="px-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>
            
            <div className="space-y-6">
              <section>
                <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
                <p className="mb-3">Welcome to Ummahti. By using our app, you agree to these terms. Please read them carefully.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">2. Definitions</h2>
                <p className="mb-3">"App" refers to Ummahti, including both our mobile application and website.</p>
                <p className="mb-3">"Service" refers to the features and functionality provided through the App.</p>
                <p className="mb-3">"User" refers to any individual who accesses or uses the App.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">3. Use of Service</h2>
                <p className="mb-3">Ummahti is a prayer and lifestyle app designed to help Muslims maintain their daily prayers and religious practices. The app provides:</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Prayer time calculations and notifications</li>
                  <li>Qibla direction</li>
                  <li>Islamic content and resources</li>
                  <li>Community features</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">4. User Accounts</h2>
                <p className="mb-3">To use certain features of the App, you must create an account. You agree to:</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Provide accurate information</li>
                  <li>Maintain the security of your account</li>
                  <li>Not share your account credentials</li>
                  <li>Notify us of any unauthorized use</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">5. User Content</h2>
                <p className="mb-3">When you submit content to the App, you retain ownership but grant us a license to use, store, and share that content as necessary to provide our services.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">6. Acceptable Use</h2>
                <p className="mb-3">You agree not to:</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Use the App for any illegal purpose</li>
                  <li>Share inappropriate or harmful content</li>
                  <li>Attempt to gain unauthorized access</li>
                  <li>Interfere with the App's operation</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">7. Prayer Times and Religious Content</h2>
                <p className="mb-3">While we strive for accuracy in prayer times and religious content, we recommend verifying critical information with local religious authorities. Prayer times are calculated based on geographical location and selected calculation methods.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">8. Intellectual Property</h2>
                <p className="mb-3">The App and its original content are protected by copyright, trademark, and other laws. Our religious content is provided with utmost respect for Islamic traditions and sources.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">9. Third-Party Services</h2>
                <p className="mb-3">The App may integrate with third-party services. Your use of such services is subject to their respective terms and conditions.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">10. Disclaimer</h2>
                <p className="mb-3">The App is provided "as is" without warranties of any kind. We are not responsible for any errors or inaccuracies in prayer times or religious content.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">11. Limitation of Liability</h2>
                <p className="mb-3">To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the App.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">12. Changes to Terms</h2>
                <p className="mb-3">We may modify these terms at any time. Continued use of the App after changes constitutes acceptance of the new terms.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">13. Contact Information</h2>
                <p className="mb-3">For questions about these terms, please contact us at support@ummahti.net</p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppTermsOfService; 